import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function BudgetDialog({ open, onClose, onSave, initialData, debtNames }) {
  const isEdit = !!initialData;
  const [form, setForm] = useState(
    initialData ? {
      date: initialData.date,
      description: initialData.description,
      category: initialData.category || "expense",
      budgeted: initialData.budgeted,
      actual: initialData.actual,
      debt_name: initialData.debt_name || "",
    } : {
      date: new Date().toISOString().split("T")[0],
      description: "",
      category: "expense",
      budgeted: 0,
      actual: 0,
      debt_name: "",
    }
  );

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      budgeted: Number(form.budgeted),
      actual: Number(form.actual),
      debt_name: form.category === "debt_payment" ? form.debt_name : "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md rounded-sm border border-gray-200" data-testid="budget-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-semibold tracking-tight">
            {isEdit ? "Edit Entry" : "Add Budget Entry"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Date</Label>
              <Input data-testid="budget-date-input" type="date" value={form.date} onChange={(e) => handleChange("date", e.target.value)} className="rounded-sm mt-1 font-mono-data" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Category</Label>
              <Select value={form.category} onValueChange={(val) => handleChange("category", val)}>
                <SelectTrigger data-testid="budget-category-select" className="rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="expense">Bill / Expense</SelectItem>
                  <SelectItem value="debt_payment">Debt Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
              <Input data-testid="budget-desc-input" value={form.description} onChange={(e) => handleChange("description", e.target.value)} className="rounded-sm mt-1" required />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Budgeted (£)</Label>
              <Input data-testid="budget-budgeted-input" type="number" step="0.01" value={form.budgeted} onChange={(e) => handleChange("budgeted", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Actual (£)</Label>
              <Input data-testid="budget-actual-input" type="number" step="0.01" value={form.actual} onChange={(e) => handleChange("actual", e.target.value)} className="rounded-sm mt-1 font-mono-data" />
            </div>
            {form.category === "debt_payment" && debtNames && debtNames.length > 0 && (
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-gray-500">Linked Debt</Label>
                <select
                  data-testid="budget-debt-select"
                  value={form.debt_name || "none"}
                  onChange={(e) => handleChange("debt_name", e.target.value === "none" ? "" : e.target.value)}
                  className="flex h-9 w-full items-center rounded-sm border border-input bg-transparent px-3 py-2 text-sm mt-1 focus:ring-1 focus:ring-black focus:border-black"
                >
                  <option value="none">No linked debt</option>
                  {debtNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm" data-testid="budget-dialog-cancel">Cancel</Button>
            <Button type="submit" className="rounded-sm bg-black text-white hover:bg-gray-800" data-testid="budget-dialog-save">{isEdit ? "Update" : "Add Entry"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
